/**
 * Switching outputs from the app. Two taps (ask, then confirm) for anything that moves water
 * or power, and the state only changes on screen once the device confirms it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type CommandRecord, type Crop, type CropRun, type Device, type Reading } from "../api.ts";
import { analogOutputs, can, FINAL, lifecycle, switchables } from "../control.ts";
import { ago, formatNumber } from "../format.ts";
import { Section, useLoad } from "../ui.tsx";
import { LockCard, useCan } from "../plan.tsx";
import { Toggle } from "../widgets.tsx";
import { portKey } from "../../../../packages/core/src/ports.ts";

/** Whether nothing can be sent to this farm (server listening beside FMS-Control, farm not cut over). */
export { useListenOnly } from "../listen.tsx";
import { useListenOnly } from "../listen.tsx";
import { OutputRules, rulesCount } from "./rules.tsx";

/** Follows commands sent from this screen until each one is confirmed or has failed. */
export function useCommandWatch(eui: string, onSettled: () => void) {
  const [cmds, setCmds] = useState<Record<string, CommandRecord>>({});
  const settled = useRef(onSettled);
  settled.current = onSettled;
  const pending = Object.values(cmds).some((c) => !FINAL.has(c.status));

  const latest = useRef(cmds);
  latest.current = cmds;

  useEffect(() => {
    if (!pending) return;
    const t = setInterval(async () => {
      // Phone in a pocket: no need to ask; the next tick after it's back shows where it got to.
      if (document.hidden) return;
      try {
        const { commands } = await api.commands(eui, 30);
        const next = { ...latest.current };
        let changed = false;
        let finished = false;
        for (const [key, c] of Object.entries(next)) {
          const fresh = commands.find((x) => x.id === c.id);
          if (fresh && fresh.status !== c.status) {
            next[key] = fresh;
            changed = true;
            if (FINAL.has(fresh.status)) finished = true;
          }
        }
        if (changed) setCmds(next);
        if (finished) settled.current();
      } catch {
        /* try again on the next tick */
      }
    }, 2000);
    return () => clearInterval(t);
  }, [eui, pending]);

  const track = useCallback((key: string, c: CommandRecord) => {
    setCmds((cur) => ({ ...cur, [key]: c }));
    if (FINAL.has(c.status)) settled.current();
  }, []);
  return { cmds, track };
}

export function Lifecycle({ c }: { c: CommandRecord }) {
  return (
    <div className="lifecycle" role="status">
      {lifecycle(c).map((s, i) => (
        <span key={i} className={`st ${s.state}`}>
          {i > 0 && <span className="sep" />}
          <i />
          {s.label}
        </span>
      ))}
      {c.failReason && <span className="fail-why">{c.failReason}</span>}
    </div>
  );
}

const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");

/**
 * After a switch request got no answer: the newest command for the device asked for since then,
 * if the server did queue it. A few tries, as the signal may come and go.
 */
async function findSent(devEui: string, since: number, known: Set<string>): Promise<CommandRecord | undefined> {
  let me: string | undefined;
  for (let i = 0; i < 4; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    try {
      me ??= (await api.me()).user.email;
      const { commands } = await api.commands(devEui, 5);
      // This person's, asked for since the tap (the phone's clock and the server's may differ a
      // little: a minute either way), and not one already on screen. Never a program's or
      // someone else's: showing that would hide that this tap was lost.
      const hit = commands.filter((c) => c.requestedBy === me && c.requestedAt >= since - 60_000 && !known.has(c.id)).sort((a, b) => b.requestedAt - a.requestedAt)[0];
      if (hit) return hit;
    } catch {
      /* still no signal: try again */
    }
  }
  return undefined;
}

export function OutputsCard({
  device,
  readings,
  role,
  now,
  noSignal = false,
  onChanged,
}: {
  device: Device;
  readings: Reading[];
  role: string;
  now: number;
  /** The page can't reach the server: what's shown may not be the device's real state, so nothing can be switched. */
  noSignal?: boolean;
  onChanged: () => void;
}) {
  const listenOnly = useListenOnly();
  // Either way nothing can be sent from here now.
  const blocked = listenOnly || noSignal;
  const { cmds, track } = useCommandWatch(device.devEui, onChanged);
  const [asking, setAsking] = useState<{ key: string; state: boolean } | null>(null);
  const [error, setError] = useState<{ key: string; text: string } | null>(null);
  // Crops: which crop a pump is switched on for, and what each output is running for now.
  const crops = useLoad(() => api.crops(device.accountId).catch(() => ({ crops: [], askCrop: false })), [device.accountId]);
  const runs = useLoad(() => api.deviceCropRuns(device.devEui).catch(() => ({ runs: [] })), [device.devEui], 30_000);
  const live = (crops.data?.crops ?? []).filter((x) => !x.archived);
  const askCrop = crops.data?.askCrop === true;
  const [cropFor, setCropFor] = useState("");
  // Valves (UC51x): open for this many minutes, then the valve closes itself; blank = stay open.
  const [runMin, setRunMin] = useState("");
  // QOTO valves: how far to open (default fully).
  const [openPct, setOpenPct] = useState("100");
  const [tagging, setTagging] = useState<string | null>(null);
  // Outputs whose command is on its way to the server (before the queue has a record of it).
  const [sending, setSending] = useState<ReadonlySet<string>>(new Set());
  // Farm rules that switch each output, with their own on/off switches (screens/rules.tsx).
  const staff = role === "fms-admin";
  const rulesAcct = staff ? device.accountId : undefined;
  const ruleList = useLoad(() => api.programs(rulesAcct).catch(() => null), [rulesAcct], 30_000);
  const [rulesOpen, setRulesOpen] = useState<string | null>(null);
  const outs = switchables(device, readings);
  const aos = analogOutputs(device, readings);
  const planOk = useCan("control");
  if (!outs.length && !aos.length) return null;
  if (!planOk) {
    return (
      <Section title="Controls">
        <LockCard feature="control">
          <div className="card list">
            {outs.map((o) => (
              <div className="ctl" key={o.key}>
                <div>
                  <div className="t">{o.label}</div>
                  <div className="d">{o.on ? "On" : "Off"}</div>
                </div>
                <div className="ctl-actions">
                  <button className="btn">Turn on</button>
                  <button className="btn">Turn off</button>
                </div>
              </div>
            ))}
          </div>
        </LockCard>
      </Section>
    );
  }
  const allowed = can(role, "control");

  const send = async (key: string, fn: () => Promise<{ command: CommandRecord }>) => {
    setAsking(null);
    setError(null);
    setSending((cur) => new Set(cur).add(key));
    const startedAt = Date.now();
    try {
      track(key, (await fn()).command);
      // Show it in the device's command list straight away, not on the next minute's refresh.
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.code === "offline") {
        // No answer on a weak signal doesn't mean it wasn't sent: the server may have queued it.
        // Look for it rather than invite a second tap that sends it twice.
        setError({ key, text: "No answer yet: it may still have been sent. Checking…" });
        const found = await findSent(device.devEui, startedAt, new Set(Object.values(cmds).map((x) => x.id)));
        if (found) {
          setError(null);
          track(key, found);
        } else setError({ key, text: "Couldn't confirm it was sent. Check your signal, then look at the output before trying again." });
      } else setError({ key, text: message(e) });
    } finally {
      setSending((cur) => {
        const next = new Set(cur);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <Section title="Controls">
      {noSignal && !listenOnly && <div className="banner warn small">Switching needs signal. The switches come back when Think-Control can be reached.</div>}
      {listenOnly && (
        <div className="banner warn small">
          Switching is off for now: Think-Control is only listening while FMS-Control still runs these devices. It's turned on at cut-over.
        </div>
      )}
      {!allowed && <div className="muted small">Your role can see outputs but not switch them.</div>}
      <div className="card list">
        {outs.map((o) => {
          const c = cmds[o.key];
          const busy = sending.has(o.key) || (c !== undefined && !FINAL.has(c.status));
          const ask = asking?.key === o.key ? asking : null;
          // An output set up with its own crops (as in FMS-Control) only offers those.
          const allowedCrops = device.ports.find((p) => portKey(p) === o.key)?.link?.crops;
          const choices = allowedCrops?.length ? live.filter((x) => allowedCrops.includes(x.id)) : live;
          return (
            <div className="ctl" key={o.key}>
              <div style={{ minWidth: 0 }}>
                <div className="t">{o.label}</div>
                <div className="d">
                  <span className={`onoff ${o.on ? "on" : ""}`}>
                    {o.on === undefined ? "Unknown" : o.percent ? (o.on ? `Open ${o.opening} %` : "Closed") : o.on ? "On" : "Off"}
                  </span>
                  {o.at ? ` · reported ${ago(o.at, now)}` : ""} · <span className="mono">{o.key}</span>
                </div>
              </div>
              {allowed && (
                <div className="ctl-actions">
                  {ask ? (
                    <>
                      {ask.state && choices.length > 0 && (
                        <select className="num-in" style={{ width: 170 }} value={cropFor} onChange={(e) => setCropFor(e.target.value)} aria-label="Crop this run is for">
                          <option value="">{askCrop ? "Which crop?" : "No crop"}</option>
                          {choices.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.name}
                            </option>
                          ))}
                          {askCrop && <option value="none">Not for a crop</option>}
                        </select>
                      )}
                      {ask.state && o.percent && (
                        <label className="small" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          open to
                          <input className="num-in" style={{ width: 70 }} inputMode="numeric" value={openPct} onChange={(e) => setOpenPct(e.target.value)} aria-label="How far to open, percent" />
                          %
                        </label>
                      )}
                      {ask.state && o.valve && (
                        <label className="small" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          for
                          <input className="num-in" style={{ width: 70 }} inputMode="numeric" value={runMin} onChange={(e) => setRunMin(e.target.value)} placeholder="∞" aria-label="Minutes to stay open" />
                          min
                        </label>
                      )}
                      <button
                        className={`btn ${ask.state ? "primary" : "danger"}`}
                        disabled={
                          blocked ||
                          (ask.state && askCrop && choices.length > 0 && !cropFor) ||
                          (o.valve && runMin.trim() !== "" && !(Number(runMin) > 0)) ||
                          (o.percent && ask.state && !(Number.isInteger(Number(openPct)) && Number(openPct) >= 1 && Number(openPct) <= 100))
                        }
                        onClick={() => {
                          const crop = ask.state && cropFor ? cropFor : undefined;
                          const seconds = o.valve && ask.state && runMin.trim() ? Math.round(Number(runMin) * 60) : undefined;
                          const percent = o.percent && ask.state && Number(openPct) !== 100 ? Number(openPct) : undefined;
                          void send(o.key, () =>
                            o.kind === "port" ? api.setPort(device.devEui, o.key, ask.state, crop, seconds, percent) : api.setOutput(device.devEui, o.kind, o.channel, ask.state, crop),
                          ).then(() => runs.reload());
                        }}
                      >
                        Confirm: {o.valve || o.percent ? (ask.state ? (o.percent && Number(openPct) !== 100 ? `open to ${openPct} %` : "open") : "close") : ask.state ? "turn on" : "turn off"}
                      </button>
                      <button className="btn" onClick={() => setAsking(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    // The switch asks for a confirm first; it only moves once the device reports the new state.
                    <>
                      {o.percent && o.on && (
                        <button className="btn" disabled={busy || blocked} onClick={() => setAsking({ key: o.key, state: true })}>
                          Change opening
                        </button>
                      )}
                      <Toggle on={o.on} pending={busy} disabled={blocked || busy} label={`${o.label}: ${o.on ? "turn off" : "turn on"}`} onChange={(state) => setAsking({ key: o.key, state })} />
                    </>
                  )}
                </div>
              )}
              {ask && (
                <div className="ctl-note">
                  {o.percent ? (ask.state ? "Open" : "Close") : ask.state ? "Turn on" : "Turn off"} <b>{o.label}</b> at {device.name}? It only shows as {o.percent ? (ask.state ? "open" : "closed") : ask.state ? "on" : "off"} once the device confirms
                  {o.valve ? " (valve controllers usually answer on their next report, which can take several minutes)" : ""}.
                </div>
              )}
              <CropLine
                device={device}
                output={o.key}
                on={o.on === true}
                run={(runs.data?.runs ?? []).find((r) => r.output === o.key && r.open)}
                crops={choices}
                canTag={allowed}
                tagging={tagging === o.key}
                onTag={(v) => setTagging(v ? o.key : null)}
                onChanged={runs.reload}
              />
              {c && <Lifecycle c={c} />}
              {error?.key === o.key && <div className="error ctl-note">{error.text}</div>}
              {o.kind !== "port" && ruleList.data && (() => {
                const n = rulesCount(ruleList.data.programs, device.devEui, o.key);
                // Switching on shows the rules straight away: that's when a farmer picks which irrigator this pump follows.
                const show = rulesOpen === o.key || (ask?.state === true && rulesOpen !== `-${o.key}`);
                return (
                  <>
                    <div className="ctl-note">
                      <button className="linkish rules-link" onClick={() => setRulesOpen(show ? `-${o.key}` : o.key)} aria-expanded={show}>
                        Rules{n.total ? `: ${n.on} of ${n.total} on` : ""} {show ? "▴" : "▾"}
                      </button>
                    </div>
                    {show && (
                      <div className="ctl-note">
                        <OutputRules device={device} outputKey={o.key} role={role} staff={staff} onChanged={ruleList.reload} />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          );
        })}
        {aos.map((o) => (
          <AnalogRow
            key={o.key}
            o={o}
            now={now}
            disabled={!allowed || blocked}
            c={cmds[o.key]}
            error={error?.key === o.key ? error.text : ""}
            onSet={(v) => void send(o.key, () => api.setAnalogOutput(device.devEui, o.channel, v))}
          />
        ))}
      </div>
    </Section>
  );
}

function AnalogRow(props: {
  o: ReturnType<typeof analogOutputs>[number];
  now: number;
  disabled: boolean;
  c: CommandRecord | undefined;
  error: string;
  onSet: (v: number) => void;
}) {
  const { o, now, c } = props;
  const [value, setValue] = useState("");
  const [asking, setAsking] = useState(false);
  const busy = c !== undefined && !FINAL.has(c.status);
  const n = Number(value);
  const range = o.port?.scale ? `${formatNumber(o.port.scale.low)}–${formatNumber(o.port.scale.high)} ${o.port.scale.unit}` : "";
  const valid = value.trim() !== "" && Number.isFinite(n) && (!o.port?.scale || (n >= Math.min(o.port.scale.low, o.port.scale.high) && n <= Math.max(o.port.scale.low, o.port.scale.high)));
  return (
    <div className="ctl">
      <div style={{ minWidth: 0 }}>
        <div className="t">{o.label}</div>
        <div className="d">
          {o.value !== undefined ? `${formatNumber(o.value)} ${o.unit ?? ""}` : "Unknown"}
          {o.at ? ` · reported ${ago(o.at, now)}` : ""} · <span className="mono">{o.key}</span>
          {range ? ` · range ${range}` : ""}
        </div>
      </div>
      {!props.disabled && (
        <div className="ctl-actions">
          {asking ? (
            <>
              <button
                className="btn primary"
                onClick={() => {
                  setAsking(false);
                  props.onSet(n);
                }}
              >
                Confirm: set {formatNumber(n)} {o.unit ?? ""}
              </button>
              <button className="btn" onClick={() => setAsking(false)}>
                Cancel
              </button>
            </>
          ) : (
            <>
              {o.port?.scale && (
                <div className="ao-slider">
                  <input
                    type="range"
                    min={Math.min(o.port.scale.low, o.port.scale.high)}
                    max={Math.max(o.port.scale.low, o.port.scale.high)}
                    step={(Math.abs(o.port.scale.high - o.port.scale.low) / 100).toPrecision(2)}
                    value={value.trim() !== "" && Number.isFinite(n) ? n : (o.value ?? o.port.scale.low)}
                    onChange={(e) => setValue(e.target.value)}
                    aria-label={`Slide to a new value for ${o.label}`}
                  />
                  <div className="ao-vals">
                    <span>{formatNumber(o.port.scale.low)} {o.unit ?? ""}</span>
                    <span>{formatNumber(o.port.scale.high)} {o.unit ?? ""}</span>
                  </div>
                </div>
              )}
              <input className="num-in" inputMode="decimal" placeholder={o.unit ?? "value"} value={value} onChange={(e) => setValue(e.target.value)} aria-label={`New value for ${o.label}`} />
              <button className="btn" disabled={busy || !valid} onClick={() => setAsking(true)}>
                Set
              </button>
            </>
          )}
        </div>
      )}
      {value.trim() !== "" && !valid && !asking && <div className="error ctl-note">Enter a number{range ? ` within ${range}` : ""}.</div>}
      {c && <Lifecycle c={c} />}
      {props.error && <div className="error ctl-note">{props.error}</div>}
    </div>
  );
}

/**
 * What an output is running for: "For Lucerne since 6:10 am" with End, or, for a pump that's on
 * with no crop (started at the board, by FMS-Control or a program), a way to tag it. Tagging
 * works while listen-only: it only records which crop the water and power are for.
 */
function CropLine(props: {
  device: Device;
  output: string;
  on: boolean;
  run: (CropRun & { crop: string; end: number; open: boolean }) | undefined;
  crops: Crop[];
  canTag: boolean;
  tagging: boolean;
  onTag: (open: boolean) => void;
  onChanged: () => void;
}) {
  const [crop, setCrop] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      props.onTag(false);
      props.onChanged();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  if (props.run) {
    const since = new Date(props.run.from).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
    return (
      <div className="ctl-note crop-line">
        <span className="crop-tag">For {props.run.crop}</span> since {since}
        {props.canTag && (
          <button className="linkish" disabled={busy} onClick={() => void act(() => api.endCropRun(props.run!.id, props.device.accountId))}>
            End
          </button>
        )}
        {error && <span className="error"> {error}</span>}
      </div>
    );
  }
  if (!props.on || !props.crops.length || !props.canTag) return null;
  return (
    <div className="ctl-note crop-line">
      {props.tagging ? (
        <>
          <select className="num-in" style={{ width: 170 }} value={crop} onChange={(e) => setCrop(e.target.value)} aria-label="Crop this pump is running for">
            <option value="">Which crop?</option>
            {props.crops.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
          <button className="btn" disabled={!crop || busy} onClick={() => void act(() => api.tagCropRun(props.device.devEui, props.output, crop))}>
            Save
          </button>
          <button className="btn ghost" onClick={() => props.onTag(false)}>
            Cancel
          </button>
        </>
      ) : (
        <button className="linkish" onClick={() => props.onTag(true)}>
          Running without a crop: say which crop
        </button>
      )}
      {error && <span className="error"> {error}</span>}
    </div>
  );
}
